import { GAME_CONSTANTS } from '../utils/Constants';
import { TowerType, UpgradeLevel } from './GameTypes';

export interface TowerStats {
  cost: number;
  hp: number;
  damage: number;
  attackSpeed: number;
  range: number;
  aoeRadius?: number;
  burnDamage?: number;
  slowAmount?: number;
  chainCount?: number;
}

export interface UpgradeStats {
  cost: number;
  damage?: number;
  attackSpeed?: number;
  range?: number;
  aoeRadius?: number;
  burnDamage?: number;
  slowAmount?: number;
  chainCount?: number;
  special?: string; // Special ability description
}

export const TOWER_STATS: Record<TowerType, TowerStats> = {
  arrow: {
    cost: GAME_CONSTANTS.TOWER_COSTS.arrow,
    hp: GAME_CONSTANTS.TOWER_HP,
    damage: 20,
    attackSpeed: 2.0,
    range: 3,
  },
  cannon: {
    cost: GAME_CONSTANTS.TOWER_COSTS.cannon,
    hp: GAME_CONSTANTS.TOWER_HP,
    damage: 10,
    attackSpeed: 1.0,
    range: 2,
    aoeRadius: 2,
  },
  sniper: {
    cost: GAME_CONSTANTS.TOWER_COSTS.sniper,
    hp: GAME_CONSTANTS.TOWER_HP,
    damage: 40,
    attackSpeed: 1 / 3, // 3 second interval
    range: 6,
  },
  fire: {
    cost: GAME_CONSTANTS.TOWER_COSTS.totem + GAME_CONSTANTS.TOWER_COSTS.fire,
    hp: GAME_CONSTANTS.TOWER_HP,
    damage: 25,
    attackSpeed: 2.0,
    range: 3,
    aoeRadius: 2,
    burnDamage: 2,
  },
  ice: {
    cost: GAME_CONSTANTS.TOWER_COSTS.totem + GAME_CONSTANTS.TOWER_COSTS.ice,
    hp: GAME_CONSTANTS.TOWER_HP,
    damage: 5,
    attackSpeed: 0, // Constant AoE
    range: 2,
    slowAmount: 0.3, // 30%
  },
  lightning: {
    cost: GAME_CONSTANTS.TOWER_COSTS.totem + GAME_CONSTANTS.TOWER_COSTS.lightning,
    hp: GAME_CONSTANTS.TOWER_HP,
    damage: 40,
    attackSpeed: 4.0,
    range: 3,
    chainCount: 4,
  },
};

export const UPGRADE_A_STATS: Record<TowerType, UpgradeStats> = {
  arrow: {
    cost: Math.floor(GAME_CONSTANTS.TOWER_COSTS.arrow * GAME_CONSTANTS.UPGRADE_A_PERCENT),
    damage: 30, // +50%
    special: '25% chance to deal double damage (Critical Hit)',
  },
  cannon: {
    cost: Math.floor(GAME_CONSTANTS.TOWER_COSTS.cannon * GAME_CONSTANTS.UPGRADE_A_PERCENT),
    damage: 20, // +10 damage
    aoeRadius: 3, // +50%
    special: 'Additional outer ring: 50% damage',
  },
  sniper: {
    cost: Math.floor(GAME_CONSTANTS.TOWER_COSTS.sniper * GAME_CONSTANTS.UPGRADE_A_PERCENT),
    damage: 50, // +25%
    attackSpeed: 2.7, // +30% attack speed (from 3.0s to 2.7s)
    special: 'Prioritizes enemies below 30% HP',
  },
  fire: {
    cost: Math.floor((GAME_CONSTANTS.TOWER_COSTS.totem + GAME_CONSTANTS.TOWER_COSTS.fire) * GAME_CONSTANTS.UPGRADE_A_PERCENT),
    special: 'Burn spreads to nearby units',
  },
  ice: {
    cost: Math.floor((GAME_CONSTANTS.TOWER_COSTS.totem + GAME_CONSTANTS.TOWER_COSTS.ice) * GAME_CONSTANTS.UPGRADE_A_PERCENT),
    range: 4, // +2 tiles
    special: 'Freezes enemies slowed for 2 seconds (1s freeze)',
  },
  lightning: {
    cost: Math.floor((GAME_CONSTANTS.TOWER_COSTS.totem + GAME_CONSTANTS.TOWER_COSTS.lightning) * GAME_CONSTANTS.UPGRADE_A_PERCENT),
    chainCount: 6, // +2
    special: '+15% damage per jump',
  },
};

export const UPGRADE_B_STATS: Record<TowerType, UpgradeStats> = {
  arrow: {
    cost: Math.floor(GAME_CONSTANTS.TOWER_COSTS.arrow * GAME_CONSTANTS.UPGRADE_B_MULTIPLIER),
    damage: 30, // +50% damage (+10)
    attackSpeed: 1.5, // +50% faster than base
    special: 'Passive: +150% attack speed for 6s (after first shot, 14s cooldown)',
  },
  cannon: {
    cost: Math.floor(GAME_CONSTANTS.TOWER_COSTS.cannon * GAME_CONSTANTS.UPGRADE_B_MULTIPLIER),
    damage: 20, // +10 damage
    special: 'Reduces target armor by 15%',
  },
  sniper: {
    cost: Math.floor(GAME_CONSTANTS.TOWER_COSTS.sniper * GAME_CONSTANTS.UPGRADE_B_MULTIPLIER),
    range: 7, // +2
    attackSpeed: 2.1, // +70% attack speed (from 3.0s to 2.1s, cumulative with A)
    special: '30% chance to execute enemies below 6% HP (Elite/Boss: 1.5x crit instead)',
  },
  fire: {
    cost: Math.floor((GAME_CONSTANTS.TOWER_COSTS.totem + GAME_CONSTANTS.TOWER_COSTS.fire) * GAME_CONSTANTS.UPGRADE_B_MULTIPLIER),
    burnDamage: 3, // +50% duration (4.5s)
    special: 'Death explosion: 5% max HP AoE at 3 tiles',
  },
  ice: {
    cost: Math.floor((GAME_CONSTANTS.TOWER_COSTS.totem + GAME_CONSTANTS.TOWER_COSTS.ice) * GAME_CONSTANTS.UPGRADE_B_MULTIPLIER),
    slowAmount: 0.5, // +20%
    special: '10% chance to shatter enemies <10% HP (instant kill)',
  },
  lightning: {
    cost: Math.floor((GAME_CONSTANTS.TOWER_COSTS.totem + GAME_CONSTANTS.TOWER_COSTS.lightning) * GAME_CONSTANTS.UPGRADE_B_MULTIPLIER),
    special: '20% chance to apply Electrified stack (+5% lightning damage per stack, max 10)',
  },
};

export const UPGRADE_C_STATS: Record<'arrow' | 'cannon' | 'sniper', UpgradeStats> = {
  arrow: {
    cost: Math.floor(GAME_CONSTANTS.TOWER_COSTS.arrow * 3.0), // 150g
    damage: 40, // +40 (total: 120 from base 80 after A+B)
    attackSpeed: 0.75, // 1.5s -> 0.75s (100% faster)
    range: 1, // +1 tile (total: 4 tiles)
    special: 'Power Shot: 4% chance to lower armor to 0% for that hit. If Cannon -15% armor is active, final armor becomes -15%. Deals 1.5% of mob\'s current HP + tower\'s critical damage.',
  },
  cannon: {
    cost: Math.floor(GAME_CONSTANTS.TOWER_COSTS.cannon * 3.0), // 225g
    damage: 30, // +30 (total: 60 from base 50 after A+B)
    aoeRadius: 1, // +1 tile (total: 4 tiles)
    attackSpeed: 0.67, // 1.0s -> 0.67s (50% faster)
    special: 'Unstable Formula: If mob killed under Armor Reduction effect, 40% chance to explode. Deals 100 flat damage + 0.5% of mob\'s max HP in 3-tile AoE.',
  },
  sniper: {
    cost: Math.floor(GAME_CONSTANTS.TOWER_COSTS.sniper * 3.0), // 300g
    damage: 60, // +60 (total: 150 from base 90 after A+B)
    attackSpeed: 1.91, // 2.1s -> 1.91s (10% faster)
    range: 2, // +2 tiles (total: 9 tiles)
    special: 'Cumulative Damage: Each consecutive shot at same mob increases damage by 2% (up to 30%). Resets on target switch/death.',
  },
};

export function getUpgradeCost(towerType: TowerType, level: UpgradeLevel): number {
  if (level === 'A') {
    return UPGRADE_A_STATS[towerType].cost;
  } else if (level === 'B') {
    return UPGRADE_B_STATS[towerType].cost;
  } else if (level === 'C') {
    if (towerType === 'arrow' || towerType === 'cannon' || towerType === 'sniper') {
      return UPGRADE_C_STATS[towerType].cost;
    }
  }
  
  return 0;
}

